// ============================================
// NEW FILE: VibeDetectionEngine.js
// Create in same folder as SmartMemoryService.js
// ============================================

export class VibeDetectionEngine {
  constructor(azureConfig) {
    this.endpoint = azureConfig.endpoint;
    this.deployment = azureConfig.deployment;
    this.apiKey = azureConfig.apiKey;
  }

  /**
   * Detect user's vibe/energy from their message
   */
  async detectUserVibe(userMessage) {
    try {
      if (!userMessage || userMessage.trim().length < 2) {
        return { vibe: 'neutral', confidence: 0.5 };
      }

      const systemPrompt = `Analyze the user's message and detect their emotional vibe/energy.

VIBES TO DETECT:
1. excited - Enthusiastic, energetic, using caps, exclamation marks, multiple emojis
2. playful - Joking, teasing, lighthearted, using humor
3. chill - Relaxed, casual, laid-back, cool
4. serious - Focused, determined, matter-of-fact
5. happy - Content, positive, upbeat but not wild
6. sad/down - Depressed, low energy, struggling
7. stressed - Anxious, worried, frustrated
8. curious - Inquisitive, asking questions, wanting to learn
9. neutral - Normal, no strong emotion

Return JSON:
{
  "vibe": "one of the vibes above",
  "confidence": 0.0-1.0,
  "intensity": 0.0-1.0,
  "hasHumor": true/false,
  "hasEmojis": true/false,
  "reasoning": "brief explanation"
}

Be precise. Analyze actual tone, not just word count.`;

      const response = await fetch(
        `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: systemPrompt
              },
              {
                role: 'user',
                content: `Analyze: "${userMessage}"`
              }
            ],
            temperature: 0.3,
            max_tokens: 100
          })
        }
      );

      if (!response.ok) {
        return this.fallbackVibeDetection(userMessage);
      }

      const data = await response.json();
      const jsonMatch = data.choices[0].message.content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        console.log('🎵 Detected vibe:', result);
        return result;
      }
      
      return this.fallbackVibeDetection(userMessage);
    } catch (error) {
      console.error('Vibe detection error:', error);
      return this.fallbackVibeDetection(userMessage);
    }
  }

  /**
   * Fallback vibe detection using pattern matching
   */
  fallbackVibeDetection(userMessage) {
    const lower = userMessage.toLowerCase();
    const hasExclamation = (userMessage.match(/!/g) || []).length > 1;
    const hasQuestion = userMessage.includes('?');
    const hasCaps = /[A-Z]{3,}/.test(userMessage);
    const hasEmojis = /[\u{1F300}-\u{1F9FF}]/u.test(userMessage);
    const length = userMessage.length;

    if (hasCaps && hasExclamation && length < 20) {
      return {
        vibe: 'excited',
        confidence: 0.8,
        intensity: 0.9,
        hasHumor: false,
        hasEmojis
      };
    }

    if (hasQuestion && length < 15 && !hasExclamation) {
      return {
        vibe: 'curious',
        confidence: 0.7,
        intensity: 0.5,
        hasHumor: false,
        hasEmojis
      };
    }

    if (length < 10 && (lower === 'hihi' || lower === 'haha' || lower === 'lol')) {
      return {
        vibe: 'playful',
        confidence: 0.85,
        intensity: 0.7,
        hasHumor: true,
        hasEmojis
      };
    }

    if (lower.includes('help') || lower.includes('problem') || lower.includes('stuck')) {
      return {
        vibe: 'stressed',
        confidence: 0.7,
        intensity: 0.6,
        hasHumor: false,
        hasEmojis
      };
    }

    if (length < 15) {
      return {
        vibe: 'chill',
        confidence: 0.75,
        intensity: 0.4,
        hasHumor: false,
        hasEmojis
      };
    }

    return {
      vibe: 'neutral',
      confidence: 0.6,
      intensity: 0.5,
      hasHumor: false,
      hasEmojis
    };
  }

  /**
   * Generate system prompt modifier based on detected vibe
   */
  buildVibeContext(vibeData) {
    const { vibe, intensity, hasHumor } = vibeData;

    const vibeInstructions = {
      excited: `The user is excited and energetic!
- Match their enthusiasm
- Use natural emojis (1-2 max) to add energy: 🔥 ⚡ 🚀 💯
- Keep responses upbeat and positive
- Short, punchy sentences
- You can use caps occasionally for emphasis (not excessively)
- Show genuine excitement about their interests
- Example: "Oh that's fire! 🔥 Let's do this!"`,

      playful: `The user is being playful and joking!
- Match their humor and lightheartedness
- Use light teasing back if natural
- Add subtle humor to responses
- Can use 1 relevant emoji if it fits
- Keep things fun and not serious
- Don't over-explain things
- Example: "Haha okay okay, I see what you're doing 😄"`,

      chill: `The user is laid-back and casual!
- Keep it relaxed and easygoing
- No need for emojis (unless they use them)
- Short, natural responses
- Sound like you're hanging out
- Don't try too hard
- No excess enthusiasm
- Example: "Yeah, that's cool. What's up?"`,

      serious: `The user is being serious and focused!
- Match their focused energy
- No emojis or casual language
- Direct and clear responses
- Get to the point quickly
- Take their concerns seriously
- Professional but warm tone
- Example: "Got it. Here's what we should do..."`,

      happy: `The user is happy and positive!
- Reflect their good mood
- Warm and friendly responses
- Can use 1 emoji if natural
- Be genuinely positive back
- Light touch of optimism
- Example: "That's awesome! I love the energy 😊"`,

      'sad/down': `The user is struggling or down.
- Be supportive and compassionate
- No forced happiness or emojis
- Listen more than suggest
- Validate their feelings
- Be genuine and warm
- Offer help if appropriate
- Example: "That sounds tough. I'm here if you need to talk."`,

      stressed: `The user is stressed or worried!
- Be calm and supportive
- No emojis or casual language
- Help them find solutions
- Be reassuring but realistic
- Short, clear responses
- Take them seriously
- Example: "Okay, let's break this down. What's the main thing?"`,

      curious: `The user is asking questions and exploring!
- Match their inquisitive energy
- Be enthusiastic about explaining
- Ask follow-ups to understand better
- Short paragraphs, easy to follow
- Use light emojis if explaining (1-2 max): 💡 🤔
- Example: "Great question! Let me explain..."`,

      neutral: `The user is being neutral/normal.
- Respond naturally without forcing energy
- No emojis unless relevant
- Match their message length roughly
- Be helpful and friendly
- Natural conversation style
- Example: "Hey, what's going on?"`
    };

    return vibeInstructions[vibe] || vibeInstructions.neutral;
  }

  /**
   * Get emoji suggestions based on vibe (used sparingly)
   */
  getVibeEmojis(vibe) {
    const emojiMap = {
      excited: ['🔥', '⚡', '🚀', '💯', '✨'],
      playful: ['😄', '😂', '🎉', '👀', '😏'],
      chill: [],
      serious: [],
      happy: ['😊', '🙂', '✨', '💚'],
      'sad/down': ['💙', '🤍'],
      stressed: [],
      curious: ['💡', '🤔', '👀'],
      neutral: []
    };

    return emojiMap[vibe] || [];
  }
}

export default VibeDetectionEngine;